import type { ShopifyProduct } from "@/lib/shopify";

export interface DiamondSpecs {
  carat: number | null;
  shape: string | null;
  color: string | null;
  clarity: string | null;
  certLab: string | null;
  certNumber: string | null;
  certUrl: string | null;
}

const SHAPES = [
  "Round",
  "Oval",
  "Emerald",
  "Cushion",
  "Princess",
  "Pear",
  "Marquise",
  "Radiant",
  "Asscher",
  "Heart",
];

const CLARITIES = ["FL", "IF", "VVS1", "VVS2", "VS1", "VS2"];
const COLORS = ["D", "E", "F"];

function findOption(node: ShopifyProduct["node"], names: string[]) {
  const opt = node.options.find((o) =>
    names.some((n) => o.name.toLowerCase().includes(n)),
  );
  // Use the variant's value if present, otherwise first option value
  const variantValue = node.variants.edges[0]?.node.selectedOptions.find((s) =>
    names.some((n) => s.name.toLowerCase().includes(n)),
  )?.value;
  return variantValue ?? opt?.values[0] ?? null;
}

export function getDiamondSpecs(product: ShopifyProduct): DiamondSpecs {
  const node = product.node;
  const haystack = `${node.title} ${node.description ?? ""}`;

  // Carat
  let carat: number | null = null;
  const caratOpt = findOption(node, ["carat", "weight"]);
  if (caratOpt) {
    const m = caratOpt.match(/([\d.]+)/);
    if (m) carat = parseFloat(m[1]);
  }
  if (carat == null) {
    const m = haystack.match(/([\d.]+)\s*ct\b/i);
    if (m) carat = parseFloat(m[1]);
  }

  // Shape
  let shape = findOption(node, ["shape", "cut"]);
  if (!shape) {
    const found = SHAPES.find((s) => new RegExp(`\\b${s}\\b`, "i").test(haystack));
    shape = found ?? null;
  } else {
    const found = SHAPES.find((s) => shape!.toLowerCase().includes(s.toLowerCase()));
    if (found) shape = found;
  }

  // Clarity
  let clarity = findOption(node, ["clarity"]);
  if (!clarity) {
    const found = CLARITIES.find((c) => new RegExp(`\\b${c}\\b`).test(haystack));
    clarity = found ?? null;
  } else {
    clarity = clarity.toUpperCase();
  }

  // Color
  let color = findOption(node, ["color", "colour"]);
  if (!color) {
    const m = haystack.match(/\b([D-K])\b(?=\s+(?:VVS|VS|SI|IF|FL))/);
    color = m?.[1] ?? null;
  } else {
    color = color.toUpperCase();
  }

  // Certificate
  let certLab: string | null = null;
  let certNumber: string | null = null;
  let certUrl: string | null = null;
  const labMatch = haystack.match(/\b(IGI|GIA|GCAL|HRD)\b/i);
  if (labMatch) certLab = labMatch[1].toUpperCase();
  const certNumMatch = haystack.match(
    /\b(?:IGI|GIA|GCAL|HRD|cert(?:ificate)?(?:\s*(?:no|number|#))?)[\s:#-]*([0-9]{6,})/i,
  );
  if (certNumMatch) certNumber = certNumMatch[1];
  const urlMatch = haystack.match(
    /https?:\/\/[^\s)]*(?:igi|gia|gcal|hrd|certificate|report)[^\s)]*/i,
  );
  if (urlMatch) certUrl = urlMatch[0];

  return { carat, shape, color, clarity, certLab, certNumber, certUrl };
}

export const SHAPE_OPTIONS = SHAPES;
export const CLARITY_OPTIONS = CLARITIES;
export const COLOR_OPTIONS = COLORS;
